import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import supabase from "../../supabase";
import { currencyValues, ivaValues } from "../../constants/paymentValues";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { fromStringToNumber } from "../../components/numberInput";
import { ErrorModal } from "../../components/ModalAlert";
import { Field, FormCard, FormGrid, SelectInput, TextInput } from "../../components/ui/form";
import { formatCurrencyManual } from "../../utils/formatters";
import { formatDateForDisplay } from "../../utils/dateUtils";
import { applyPendingChequesBalance, getPendingChecksTotal } from "../../utils/chequesCash";

const DetailCashExtraction = ({ 
  onCashIdSelectedChange, 
  onAmountToWithdrawChange, 
  onDescriptionChange, 
  updateCashesExtraction, 
  setSelectedChecksNumbers,
  setSelectedChecks,
  onValidateFields,
  onCheckDestinationChange
}) => {
  const [cashId, setCashId] = useState('');
  const [cashIdSelected, setCashIdSelected] = useState('');
  const [description, setDescription] = useState('');
  const [amountToWithdraw, setAmountToWithdraw] = useState('');
  const [cashes, setCashes] = useState([]);
  const [cashBalance, setCashBalance] = useState('');
  const [CheckDetails, setCheckDetails] = useState([]);
  const [selectedCheckDetails, setSelectedCheckDetails] = useState([]);
  const [paymentDetail, setPaymentDetail] = useState([]);
  const [editDisable, setEditDisable] = useState(false);
  const [maxAmount, setMaxAmount] = useState(0);
  const [checkDestination, setCheckDestination] = useState('');

  const handleAmountToWithdrawChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+(\.\d{1,2})?$/.test(value)) {
      setAmountToWithdraw(value);
      onAmountToWithdrawChange(value);
    } else {
      e.preventDefault();
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    onDescriptionChange(value);
  };

  const handleCheckDestinationChange = (e) => {
    const value = e.target.value;
    setCheckDestination(value);
    if (onCheckDestinationChange) {
      onCheckDestinationChange(value);
    }
  };

  const fetchData = async () => {
    const { data, error } = await supabase.from('cash').select('*').order('cashId', {ascending: true});
    if (error) {
      console.error("Error al obtener los datos: ", error);
      return;
    }
    try {
      const pendingTotal = await getPendingChecksTotal();
      const cashesWithPending = applyPendingChequesBalance(data, pendingTotal);
      setCashes(cashesWithPending);
      updateCashesExtraction(cashesWithPending);
    } catch (pendingError) {
      console.error("Error al calcular el total de cheques pendientes: ", pendingError);
      setCashes(data);
      updateCashesExtraction(data);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCashIdChange = async (e) => {
    const cashId = e.target.value;
    const selectedCash = cashes.find(cash => cash.cashId == cashId);
    const cashBalanceSelected = selectedCash?.balance || '0';
    
    setCashIdSelected(cashId);
    onCashIdSelectedChange(cashId);
    
    // Establecer el monto máximo que se puede retirar (solo para mostrar)
    const maxBalanceValue = parseFloat(cashBalanceSelected);
    setMaxAmount(maxBalanceValue);
    
    // Sugerimos el monto completo pero no forzamos
    setAmountToWithdraw(cashBalanceSelected);
    onAmountToWithdrawChange(cashBalanceSelected);
    
    setEditDisable(cashId === '7');
    setSelectedCheckDetails([]);
    setSelectedChecksNumbers('');
    if (setSelectedChecks) {
      setSelectedChecks([]);
    }

    if (cashId === '7') {
      setAmountToWithdraw(0);
      onAmountToWithdrawChange(0);
      const { data, error } = await supabase
        .from('paymentDetails')
        .select("paymentDetailsIdid, checkIssuerName, checkNumber, checkBank, checkAmount, checkEmitionDate, checkPaymentDate")
        .eq('paymentMethod', 7)
        .eq('checkState', 0);
      if (error) {
        console.error("Error al obtener los detalles de pago: ", error);
      } else {
        setCheckDetails(data);
      }
    } else {
      setCheckDetails([]);
    }
  };

  const syncSelectedChecks = (updatedSelectedCheckDetails) => {
    const selectedChecks = updatedSelectedCheckDetails
      .map((id) => CheckDetails.find((check) => check.paymentDetailsIdid === id))
      .filter(Boolean);

    setSelectedChecksNumbers(selectedChecks.map((check) => check.checkNumber).filter(Boolean).join(' - '));
    if (setSelectedChecks) {
      setSelectedChecks(selectedChecks);
    }
  };

  const handlePaymentDetailChange = (paymentDetailId, amount) => {
    const isChecked = selectedCheckDetails.includes(paymentDetailId);

    let updatedSelectedCheckDetails;
    let newAmountToWithdraw;
    
    if (!isChecked) {
        updatedSelectedCheckDetails = [...selectedCheckDetails, paymentDetailId];
        newAmountToWithdraw = parseFloat(amountToWithdraw || 0) + parseFloat(amount);
    } else {
        updatedSelectedCheckDetails = selectedCheckDetails.filter(id => id !== paymentDetailId);
        newAmountToWithdraw = parseFloat(amountToWithdraw || 0) - parseFloat(amount);
    }

    if (newAmountToWithdraw > maxAmount) {
        alert('El monto total a retirar no puede exceder el saldo disponible');
        return;
    }

    setAmountToWithdraw(newAmountToWithdraw);
    onAmountToWithdrawChange(newAmountToWithdraw);
    setSelectedCheckDetails(updatedSelectedCheckDetails);
    syncSelectedChecks(updatedSelectedCheckDetails);
  };

  // Agregar validación de campos requeridos
  const validateFields = () => {
    const errors = [];
    
    if (!checkDestination) {
        errors.push('Debe seleccionar un destino para el cheque');
    }
    if (!description) {
        errors.push('Debe ingresar una descripción');
    }
    if (amountToWithdraw <= 0 || amountToWithdraw === '') {
        errors.push('El monto a retirar debe ser mayor a 0');
    }
    if (amountToWithdraw > maxAmount) {
        errors.push(`El monto a retirar (${amountToWithdraw}) no puede exceder el saldo disponible (${maxAmount})`);
    }

    if (errors.length > 0) {
        return errors.join('. ');
    }
    return null;
  };

  // Exponer la función de validación al componente padre
  useEffect(() => {
    if (onValidateFields) {
      onValidateFields(validateFields);
    }
  }, [checkDestination, description, amountToWithdraw, maxAmount, onValidateFields]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", width: "100%" }}>
      <FormCard
        title="Datos del retiro"
        subtitle="Elegí la caja y el monto. Si salen cheques, se eligen abajo uno por uno."
      >
        <FormGrid columns={3}>
          <Field label="Caja" required>
            <SelectInput id="cashIdSelected" onChange={handleCashIdChange} defaultValue="" required>
              <option value="">Seleccionar caja</option>
              {Array.isArray(cashes) &&
                cashes.map((cash) => (
                  <option key={cash.cashId} value={cash.cashId}>
                    {cash.description}
                  </option>
                ))}
            </SelectInput>
          </Field>

          {cashIdSelected === '7' && (
            <Field label="Destino" required>
              <SelectInput
                id="checkDestination"
                onChange={handleCheckDestinationChange}
                value={checkDestination}
                required
              >
                <option value="">Seleccionar destino</option>
                <option value="efectivo">Efectivo</option>
                <option value="banco">Banco</option>
                <option value="cartera">Sale de cartera</option>
              </SelectInput>
            </Field>
          )}

          <Field
            label="Monto"
            required
            hint={cashIdSelected ? `Saldo disponible: ${formatCurrencyManual(maxAmount)}` : undefined}
          >
            <TextInput
              type="number"
              id="amountToWithdraw"
              value={amountToWithdraw}
              min="0"
              step="0.01"
              numeric
              placeholder="0,00"
              onChange={handleAmountToWithdrawChange}
              disabled={editDisable}
              required
            />
          </Field>

          <Field label="Descripción" required span={cashIdSelected === '7' ? 3 : 1}>
            <TextInput
              type="text"
              id="description"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Motivo del retiro"
              required
            />
          </Field>
        </FormGrid>
      </FormCard>

      <Box>
        {cashIdSelected === '7' && (
            <Box>
              <h4>Seleccione uno o más Cheques:</h4>                
              <Box className="order-detail-form-item-container finished-orders-container" style={{minHeight: paymentDetail.length === 0 ? '200px': 'auto'}}>
                <Box className="table-container">
                  <Box className="table table-header">
                    <div className="div1 item" style={{width: '5vw'}}>
                      <h4 style={{ fontWeight: 800}}>Sel</h4>
                    </div>
                    <div className="div2 item" style={{width: '20vw'}}>
                      <h4 style={{ fontWeight: 800}}>Titular</h4>
                    </div>
                    <div className="div3 item" style={{width: '20vw'}}>
                      <h4 style={{ fontWeight: 800}}>Banco</h4>
                    </div>
                    <div className="div4 item" style={{width: '20vw'}}>
                      <h4 style={{ fontWeight: 800}}>Nro Cheque</h4>
                    </div>
                    <div className="div5 item" style={{width: '20vw'}}>
                      <h4 style={{ fontWeight: 800}}>Fecha de Cobro</h4>
                    </div>
                    <div className="div6 item" style={{width: '10vw'}}>
                      <h4 style={{ fontWeight: 800}}>Monto</h4>
                    </div>
                  </Box>
                  <Box className="table">
                    {Array.isArray(CheckDetails) && CheckDetails.map((check) => {
                      return (
                        <React.Fragment key={check.paymentDetailsIdid}>
                          <div className="div1 item" style={{width: '5vw'}}>
                            <input type="checkbox" id={check.paymentDetailsIdid} value={check.checkAmount} onChange={() => handlePaymentDetailChange(check.paymentDetailsIdid, check.checkAmount)} checked={selectedCheckDetails.includes(check.paymentDetailsIdid)} />
                          </div>
                          <div className="div2 item" style={{width: '20vw'}}>
                            <h4>{check.checkIssuerName}</h4>
                          </div>
                          <div className="div3 item" style={{width: '20vw'}}>
                          <h4>{check.checkBank}</h4>
                          </div>
                          <div className="div4 item" style={{width: '20vw'}}>
                          <h4>{check.checkNumber}</h4>
                          </div>
                          <div className="div5 item" style={{width: '20vw'}}>
                          <h4>{formatDateForDisplay(check.checkPaymentDate) ? new Date(formatDateForDisplay(check.checkPaymentDate) + 'T12:00:00.000Z').toLocaleDateString('es-AR') : ''}</h4>
                          </div>
                          <div className="div6 item" style={{width: '10vw'}}>
                            <h4>{formatCurrencyManual(check.checkAmount)}</h4>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default DetailCashExtraction;
